/**
 * Product analytics — PostHog. Captures screen views, button taps, and
 * funnels for the dashboard side. Identity is the same `installation_id`
 * the backend uses, so server-side data and PostHog cohorts agree on
 * who's who without an extra `identify` round-trip.
 *
 * Note: PostHog is for the dashboard. Server-side notification triggers
 * run off Postgres state (follows / plans / users.last_seen_at), not
 * PostHog cohorts — see Spikes/anonymous-users-and-notifications.md §4.
 *
 * Capacitor renders the app in a WebView, so the browser SDK
 * (`posthog-js`) is the right fit; React Native SDK does not apply.
 */

import posthog from 'posthog-js';
import { logger } from './logger';

const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://us.i.posthog.com';

let initialized = false;

export function initAnalytics(installationId: string): void {
  if (initialized) return;
  if (!KEY) {
    // Local dev / tests / web preview without PostHog configured —
    // analytics is optional, never block the app.
    logger.info('analytics: VITE_POSTHOG_KEY not set, skipping init');
    return;
  }
  try {
    posthog.init(KEY, {
      api_host: HOST,
      // On web, HOST is a same-origin reverse-proxy path (`/wc-relay`) so
      // events dodge ad blockers; ui_host then has to point at PostHog's
      // real domain or the toolbar/links break. Harmless when HOST is
      // already us.i.posthog.com (the iOS build talks to PostHog direct).
      ui_host: 'https://us.posthog.com',
      // Seed the device's distinct id from the durable installation id so a
      // returning visitor on the same browser stays one person. We do NOT mark
      // it identified — that's reserved for the account user id (identifyUser),
      // so signing in MERGES this device into the cross-device account person.
      bootstrap: { distinctID: installationId },
      // Record sessions, but keep PII out of the replay: mask every text
      // input (default) so emails/passwords never hit the recording, and
      // scrub console logs that might carry tokens. Opt specific elements
      // back in with `ph-no-mask` only when we're sure they're safe.
      disable_session_recording: false,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '[data-ph-mask]',
        // Block MapLibre maps from the replay. Their WebGL canvas never
        // renders in rrweb anyway, but pan/zoom fires a flood of DOM
        // mutations that rrweb rate-limits ("Too many mutations on node…").
        // Blocking the whole `.maplibregl-map` container stops that traffic
        // at the source — lower payload, no console spam, just a placeholder
        // where the (already-blank) map would be. Covers EventsMapView +
        // VenuePickerMap and any future map.
        blockSelector: '.maplibregl-map',
      },
      enable_recording_console_log: false,
      // Explicit semantic events only (see EVENTS below). Autocapture's raw
      // DOM-click firehose and the auto $pageview (unreliable in this
      // history-based SPA) are pure noise next to named events — we emit a
      // `screen_viewed` ourselves instead.
      autocapture: false,
      capture_pageview: false,
      persistence: 'localStorage+cookie',
    });
    // No identify() here — the device stays anonymous (seeded by installation
    // id) until we know the active user id; the store calls identifyUser once
    // /v1/me resolves.
    initialized = true;
  } catch (err) {
    logger.warn('analytics init failed', { err: String(err) });
  }
}

/**
 * Canonical event names. Keep all `track()` calls referencing these so the
 * PostHog taxonomy stays typo-free and discoverable in one place.
 */
export const EVENTS = {
  // Navigation
  screenViewed: 'screen_viewed',
  // Follow funnel
  teamFollowed: 'team_followed',
  teamUnfollowed: 'team_unfollowed',
  // Watch-party plans
  watchPartyPlanned: 'watch_party_planned',
  watchPartyCancelled: 'watch_party_cancelled',
  // Events
  eventRsvpAdded: 'event_rsvp_added',
  eventRsvpRemoved: 'event_rsvp_removed',
  eventDetailOpened: 'event_detail_opened',
  eventsViewToggled: 'events_view_toggled',
  // Venues
  venueSaved: 'venue_saved',
  venueUnsaved: 'venue_unsaved',
  mapCountryFiltered: 'map_country_filtered',
  mapTodayFiltered: 'map_today_filtered',
  venueSearchSelected: 'venue_search_selected',
  // Sharing & supply-side
  sharePressed: 'share_pressed',
  shareCompleted: 'share_completed',
  submitCtaTapped: 'submit_cta_tapped',
  // Health / ops (surface the venue stale-cache guards in the dashboard)
  venuesPartialLoad: 'venues_partial_load',
  allVenuesLoadFailed: 'all_venues_load_failed',
} as const;

export type AnalyticsEvent = (typeof EVENTS)[keyof typeof EVENTS];

/**
 * Identify the active user — the account `userId` when signed in (same across
 * devices), else the device's own user id. PostHog merges the current
 * anonymous person into this id, so one account collapses to one person across
 * devices while a returning same-browser visitor stays one person too.
 */
export function identifyUser(userId: string, props?: Record<string, unknown>): void {
  if (!initialized) return;
  try {
    posthog.identify(userId, props);
  } catch (err) {
    logger.warn('analytics identify failed', { err: String(err) });
  }
}

/**
 * Drop the current identity. Call ONLY when switching to a *different* account
 * (so account A and B don't merge) — not on a plain sign-out, where we keep the
 * device's continuity.
 */
export function resetIdentity(): void {
  if (!initialized) return;
  try {
    posthog.reset();
  } catch (err) {
    logger.warn('analytics reset failed', { err: String(err) });
  }
}

export function track(event: AnalyticsEvent, props?: Record<string, unknown>): void {
  if (!initialized) return;
  try {
    posthog.capture(event, props);
  } catch (err) {
    logger.warn('analytics capture failed', { event, err: String(err) });
  }
}
